import { useEstabsContext } from '../../contexts/EstabsContext';
import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAppContext } from '../../contexts/AppContext';
import { useParamsContext } from '../../contexts/ParamsContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { useVendasContext } from './Context';
import {
    PAYMENT_METHOD_LABELS,
    PAYMENT_STATUS_LABELS,
    formatDateTime,
    formatWarning,
    getApiErrorMessage,
    getDiscountRuleLabel,
} from './helpers';
import { SaleItemsGroup, SaleTotals } from './SaleParts';
import { VendasService } from './Service';
import { TriangleAlert } from 'lucide-react';

const service = new VendasService();

export default function SaleDetail() {
    const { estabSession } = useEstabsContext();
    const { id, clear } = useParamsContext();
    const { reloadVendas, reloadRecorrentes } = useVendasContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [venda, setVenda] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [removing, setRemoving] = useState(false);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setLoadError('');
        try {
            const response = await service.getById(id);
            setVenda(response?.data || null);
        } catch (error: any) {
            console.error(error);
            setLoadError(error?.response?.data?.message || 'Não foi possível carregar a venda');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleRemove = async () => {
        try {
            setRemoving(true);
            await service.remove(venda._id);
            successSnackbar('Venda removida. O estoque foi devolvido.');
            await Promise.all([reloadVendas(), reloadRecorrentes()]);
            clear();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(getApiErrorMessage(error, 'Não foi possível remover a venda.'));
            setRemoving(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto w-full max-w-2xl space-y-4 px-2" aria-busy="true">
                <div className="sf-skeleton h-32" />
                <div className="sf-skeleton h-24" />
            </div>
        );
    }

    if (loadError || !venda) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>{loadError || 'Venda não encontrada'}</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void load()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    const canceled = venda.status === 'CANCELADA';
    const payment = [PAYMENT_STATUS_LABELS[venda.paymentStatus], PAYMENT_METHOD_LABELS[venda.paymentMethod]]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="sf-page mx-auto max-w-2xl space-y-5 px-2">
            <div className="py-2 text-center">
                <p className="text-3xl font-semibold tabular-nums">{formatBRL(venda.total)}</p>
                <p className="mt-1 text-sm text-base-content/60">{formatDateTime(venda.createdAt, estabSession?.timezone)}</p>
                {canceled && (
                    <span className="sf-tags justify-center">
                        <span className="sf-tag" data-tone="gray">
                            Cancelada
                        </span>
                    </span>
                )}
            </div>

            <div className="sf-group">
                <div className="sf-info-row">
                    <span>Cliente</span>
                    <span>{venda.clientName || 'Sem cliente'}</span>
                </div>
                <div className="sf-info-row">
                    <span>Origem</span>
                    <span>{venda.source === 'RECORRENTE' ? 'Recorrência' : 'Venda manual'}</span>
                </div>
                {!!payment && (
                    <div className="sf-info-row">
                        <span>Pagamento</span>
                        <span>{payment}</span>
                    </div>
                )}
            </div>

            <section>
                <h2 className="sf-group-title">Itens</h2>
                <SaleItemsGroup items={venda.items} />
            </section>

            <SaleTotals
                subTotal={Number(venda.subTotal || 0)}
                discountAmount={Number(venda.discountAmount || 0)}
                discountRule={getDiscountRuleLabel(venda.discountType, venda.discountValue)}
                total={Number(venda.total || 0)}
            />

            {!!venda.warnings?.length && (
                <div className="sf-group p-4 text-sm text-base-content/70">
                    <p className="font-semibold text-warning">Avisos</p>
                    <ul className="mt-1 list-disc pl-4">
                        {venda.warnings.map((warning: string) => (
                            <li key={warning}>{formatWarning(warning)}</li>
                        ))}
                    </ul>
                </div>
            )}

            {!canceled && (
                <div className="sf-group">
                    <button type="button" className="sf-destructive-row" onClick={() => setConfirmOpen(true)}>
                        Remover venda
                    </button>
                </div>
            )}

            {confirmOpen && (
                <ConfirmDialog
                    title="Remover esta venda?"
                    confirmLabel="Remover venda"
                    cancelLabel="Voltar"
                    loading={removing}
                    onConfirm={() => void handleRemove()}
                    onClose={() => setConfirmOpen(false)}
                >
                    A venda de {formatBRL(venda.total)} sai da lista e dos indicadores, o estoque baixado volta para o
                    saldo e a receita é estornada no financeiro.
                </ConfirmDialog>
            )}
        </div>
    );
}
